import type { IndexedLine } from 'src/components/BasicComponents/Maps/helpers/distanceMarkers';
import { BASE_BLUE, type Tuple3 } from 'src/lib/player/constants';
import type { TrackType } from 'src/lib/player/store';
import { validateColor } from 'src/lib/player/utils';

const SELECTED_LINE_WIDTH_FACTOR = 1.7;

export type TrackLayerProps = {
  id: string;
  coordinates: Array<Tuple3>;
  color: string;
  lineWidth: number;
};

/**
 * The player's tracks in draw order: selected ones last, and the hovered one last of all. The
 * hovered track needs its own tier because `getSelectedIds` already counts it as selected.
 */
export function buildTrackLayerProps(
  tracks: Array<TrackType>,
  selectedIds: Set<string>,
  highlightedId: string | null = null,
): Array<TrackLayerProps> {
  const tier = (track: TrackType) =>
    track.id === highlightedId ? 2 : selectedIds.has(track.id) ? 1 : 0;
  // Stable, so tracks within a tier keep their original order.
  const ordered = [...tracks].sort((a, b) => tier(a) - tier(b));

  return ordered.map((track) => ({
    id: track.id,
    coordinates: track.coordinates,
    color: validateColor(track.markerColor.rgba) ? track.markerColor.rgba : BASE_BLUE,
    lineWidth: selectedIds.has(track.id)
      ? track.lineWidth * SELECTED_LINE_WIDTH_FACTOR
      : track.lineWidth,
  }));
}

/** A geo id is only unique within its event, so the player keys every track by both. */
const trackKey = (trackId: string | null, eventIndex: number): string => `${trackId}_${eventIndex}`;

/** Structural, so `EventState` satisfies it and a spec needs no event fixture. */
export type MarkerSourceEvent = {
  index: number;
  shadowTrackId: string | null;
  chunkedShadowTrack: { size: () => number; getLngLat: (index: number) => [number, number] } | null;
  options: { showDistanceMarkers: boolean };
};

/**
 * The line each event measures its distance markers along, keyed by the track that draws it: the
 * shadowtrack, which reaches the player as an ordinary track feature.
 */
export function markerLinesByTrack(events: Array<MarkerSourceEvent>): Map<string, IndexedLine> {
  const lines = new Map<string, IndexedLine>();

  for (const event of events) {
    const track = event.chunkedShadowTrack;
    // `shadowgeojsonHash` and `shadowTrackId` are serialized independently, so the chunked track
    // can load for an event that never says which of its tracks it belongs to.
    if (!event.options.showDistanceMarkers || track == null || event.shadowTrackId == null)
      continue;

    lines.set(trackKey(event.shadowTrackId, event.index), {
      length: track.size(),
      at: (index) => track.getLngLat(index),
    });
  }

  return lines;
}

/**
 * The one marker group to show while hovering, or null for "show them all" — which is what
 * hovering anything without markers of its own, a starter or a POI, has to leave.
 */
export function focusedMarkerTrackId(
  groupTrackIds: Array<string>,
  highlightedId: string | null,
): string | null {
  return highlightedId != null && groupTrackIds.includes(highlightedId) ? highlightedId : null;
}

/**
 * Whether `actual` already lists the layers in `desired` order. Reordering dirties the style and
 * fires another styledata, so the caller has to stop once the order is right.
 */
export function needsReorder(actual: Array<string>, desired: Array<string>): boolean {
  const relevant = actual.filter((id) => desired.includes(id));

  return relevant.length !== desired.length || relevant.some((id, i) => id !== desired[i]);
}
